import { Check, Shield, Trash2, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TelegramChatSummary } from '@/Config/api/queryClient';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';

export const TELEGRAM_CHAT_STATE_PENDING = 'PENDING';
export const TELEGRAM_CHAT_TYPE_GROUP = 'group';
export const TELEGRAM_CHAT_TYPE_SUPERGROUP = 'supergroup';

// Filter again client-side while older pods can still list direct-link visitors.
export const isTelegramGroupChat = (chat: TelegramChatSummary): boolean =>
  chat.chat_type === TELEGRAM_CHAT_TYPE_GROUP || chat.chat_type === TELEGRAM_CHAT_TYPE_SUPERGROUP;

export interface TelegramChatRowProps {
  chat: TelegramChatSummary;
  onToggleEnabled: (chatId: string, enabled: boolean) => void;
  onRemove: (chat: TelegramChatSummary) => void;
  onConfirm: (chat: TelegramChatSummary) => void;
  /** Disables the row's controls while one of its own mutations is in flight. */
  disabled?: boolean;
  /** Confirming and switching a chat back on both 503 while the shared bot is down. */
  readinessBlocked?: boolean;
}

// A pending chat reads as the action it is waiting on: it stays silent until confirmed.
export const TelegramChatRow = ({
  chat,
  onToggleEnabled,
  onRemove,
  onConfirm,
  disabled,
  readinessBlocked,
}: TelegramChatRowProps) => {
  const { t } = useTranslation(['integration']);
  const isPending = chat.state === TELEGRAM_CHAT_STATE_PENDING;
  const title = chat.title || t('integration:telegram.oneclick.untitled_chat');

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border p-4 ${
        isPending ? 'border-warning/30 bg-warning-muted' : 'border-border bg-card'
      }`}
      data-testid={`telegram-chat-row-${chat.chat_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p
            className="truncate font-medium text-foreground"
            data-testid={`telegram-chat-title-${chat.chat_id}`}
          >
            {title}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-xs">
            <span>{t(`integration:telegram.oneclick.chat_type.${chat.chat_type}`)}</span>
            {chat.member_count !== null && (
              <span className="flex items-center gap-1">
                <Users className="size-3" />
                {chat.member_count}
              </span>
            )}
            {chat.bot_is_admin && (
              <span
                className="flex items-center gap-1"
                data-testid={`telegram-chat-admin-${chat.chat_id}`}
              >
                <Shield className="size-3" />
                {t('integration:telegram.oneclick.is_admin')}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {isPending ? (
            <Button
              size="sm"
              onClick={() => onConfirm(chat)}
              disabled={disabled || readinessBlocked}
              aria-label={t('integration:telegram.oneclick.confirm_label', { chat: title })}
              data-testid={`telegram-chat-confirm-${chat.chat_id}`}
            >
              <Check className="size-4" />
              {t('integration:telegram.oneclick.confirm')}
            </Button>
          ) : (
            <Switch
              id={`telegram-chat-${chat.chat_id}-enabled`}
              checked={chat.enabled}
              onCheckedChange={(checked) => onToggleEnabled(chat.chat_id, checked)}
              disabled={disabled || (readinessBlocked && !chat.enabled)}
              aria-label={t('integration:telegram.oneclick.enable_label', { chat: title })}
              data-testid={`telegram-chat-toggle-${chat.chat_id}`}
            />
          )}
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => onRemove(chat)}
            disabled={disabled}
            aria-label={t('integration:telegram.oneclick.remove_label', { chat: title })}
            data-testid={`telegram-chat-remove-${chat.chat_id}`}
          >
            <Trash2 />
          </IconButton>
        </div>
      </div>

      {isPending && (
        <p
          className="m-0 text-muted-foreground text-xs"
          data-testid={`telegram-chat-pending-note-${chat.chat_id}`}
        >
          {t('integration:telegram.oneclick.pending_note', {
            username: chat.connected_by_username || t('integration:telegram.oneclick.someone'),
          })}
        </p>
      )}
    </div>
  );
};

export default TelegramChatRow;
